import { useGetUserProfile } from '@leagueplatform/web-common';

type ProfileField = {
  labelId: `STR_${string}`;
  value: string | undefined;
  forceLtr?: boolean;
  sensitive?: boolean;
};

/**
 * `custom_fields` key the health card number arrives under. It is not a first-class
 * `user_profile` field — the backend syncs it from Epic as a named custom field
 * (TRIL-492, matching iOS TRIL-331 / Android TRIL-332).
 */
const HEALTH_CARD_CUSTOM_FIELD_NAME = 'ohip_identifier';

export const usePersonalInfo = () => {
  const { data: profile, isLoading, isError, refetch } = useGetUserProfile();

  const formattedDateOfBirth = profile?.userProfile?.dateOfBirth
    ? (() => {
        const date = new Date(profile.userProfile!.dateOfBirth!);
        if (Number.isNaN(date.getTime()))
          return profile.userProfile!.dateOfBirth!;
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${month}/${day}/${year}`;
      })()
    : undefined;

  const fields: ProfileField[] = profile
    ? (
        [
          {
            labelId: 'STR_PREFERRED_NAME',
            value: profile.userProfile?.preferredFirstName,
          },
          { labelId: 'STR_FIRST_NAME', value: profile.userProfile?.firstName },
          { labelId: 'STR_LAST_NAME', value: profile.userProfile?.lastName },
          {
            labelId: 'STR_DATE_OF_BIRTH',
            value: formattedDateOfBirth,
          },
          {
            labelId: 'STR_POSTAL_CODE',
            value: profile.userProfile?.locations?.[0]?.postal,
          },
          { labelId: 'STR_EMAIL_ADDRESS', value: profile.email },
          // Health card is alphanumeric — force LTR to prevent bidi reversal in RTL layouts.
          {
            labelId: 'STR_HEALTH_CARD_NUMBER',
            value:
              profile.customFields?.find(
                (f) => f.name === HEALTH_CARD_CUSTOM_FIELD_NAME,
              )?.value ?? '',
            forceLtr: true,
            sensitive: true,
          },
        ] as ProfileField[]
      ).filter((f) => Boolean(f.value))
    : [];

  return {
    fields,
    isLoading,
    isError: isError || !profile,
    refetch,
  };
};
